import { useMemo, useState } from 'react';
import { PrefsScope, prefsStorageKey, readPrefsValue, writePrefsValue } from './prefsStorage';
import { warnInDev } from './warnInDev';

export type CrudDensity = 'comfortable' | 'compact';

export interface ColumnPrefs {
  order: string[];
  hidden: string[];
  density: CrudDensity;
}

/** The column metadata the prefs layer needs — the subset of CrudColumn it reads. */
export interface PrefsColumn {
  key: string;
  alwaysVisible?: boolean;
  /** Hidden until the user shows it, and only before any preference is stored. */
  defaultHidden?: boolean;
}

export function storageKey(resourceKey: string, scope: PrefsScope): string {
  return prefsStorageKey('crud.cols', resourceKey, scope);
}

/**
 * Re-stamps user-stored preferences against the CURRENT column config (spec §6.2). Every
 * rule is mandatory:
 *
 * - a stored key absent from config is dropped from `order` and `hidden`;
 * - a config key absent from `order` is appended (visible) — a column added in an update
 *   must not inherit a stale blob's silence;
 * - `alwaysVisible` keys are removed from `hidden`;
 * - unparseable/absent JSON resolves to config defaults without throwing;
 * - duplicates in `order` collapse to the first occurrence.
 *
 * A key listed in `hidden` but absent from stored `order` is still honoured as hidden:
 * `hidden` is the user's explicit choice, and the manager always writes both lists.
 */
export function reconcileColumnPrefs(configKeys: string[], alwaysVisibleKeys: string[], stored: unknown): ColumnPrefs {
  const valid = new Set(configKeys);
  const alwaysVisible = new Set(alwaysVisibleKeys);

  const raw = (stored && typeof stored === 'object' ? stored : {}) as Partial<ColumnPrefs>;
  const storedOrder = Array.isArray(raw.order) ? raw.order.filter((key): key is string => typeof key === 'string') : [];
  const storedHidden = Array.isArray(raw.hidden)
    ? raw.hidden.filter((key): key is string => typeof key === 'string')
    : [];

  const order: string[] = [];
  const seen = new Set<string>();
  for (const key of storedOrder) {
    if (valid.has(key) && !seen.has(key)) {
      seen.add(key);
      order.push(key);
    }
  }
  for (const key of configKeys) {
    if (!seen.has(key)) {
      seen.add(key);
      order.push(key);
    }
  }

  const hidden: string[] = [];
  for (const key of storedHidden) {
    if (valid.has(key) && !alwaysVisible.has(key) && !hidden.includes(key)) {
      hidden.push(key);
    }
  }

  // An unknown stored value degrades rather than reaching Carbon's `size` prop.
  const density: CrudDensity = raw.density === 'compact' ? 'compact' : 'comfortable';

  return { order, hidden, density };
}

/**
 * Reads this resource's stored prefs from localStorage, reconciled against the config.
 * Guards `typeof localStorage === 'undefined'` (jsdom always has it; SSR and non-browser
 * consumers may not) and never throws: corrupt JSON degrades to config defaults.
 * Absent storage ALSO falls back to config defaults — including `defaultHidden`, which by
 * design only applies before the user has expressed any preference.
 */
export function readColumnPrefs(resourceKey: string, columns: PrefsColumn[], scope: PrefsScope): ColumnPrefs {
  const configKeys = columns.map((column) => column.key);
  const alwaysVisibleKeys = columns.filter((column) => column.alwaysVisible).map((column) => column.key);
  const defaults = (): ColumnPrefs => ({
    order: configKeys,
    hidden: columns.filter((column) => column.defaultHidden && !column.alwaysVisible).map((column) => column.key),
    density: 'comfortable',
  });

  const raw = readPrefsValue('crud.cols', resourceKey, scope);
  if (raw === null) {
    return defaults();
  }
  try {
    return reconcileColumnPrefs(configKeys, alwaysVisibleKeys, JSON.parse(raw));
  } catch {
    warnInDev(`column preferences for "${resourceKey}" are corrupt; showing the config defaults.`);
    return defaults();
  }
}

export function writeColumnPrefs(resourceKey: string, prefs: ColumnPrefs, scope: PrefsScope): void {
  writePrefsValue('crud.cols', resourceKey, scope, JSON.stringify(prefs));
}

/**
 * CrudPage owns the preferences (it knows resource.key) and drives CrudTable with the
 * resolved columns. The hook holds the STORED prefs; the effective prefs are re-reconciled
 * against the current config on every render, so a column added to a page config appears
 * without the user touching their storage. Every change persists immediately, so a reload
 * — or React Query remount — keeps the user's view.
 */
export function useColumnPrefs(
  resourceKey: string,
  columns: PrefsColumn[],
  scope: PrefsScope
): [ColumnPrefs, (next: ColumnPrefs) => void] {
  const [stored, setStored] = useState<ColumnPrefs>(() => readColumnPrefs(resourceKey, columns, scope));
  // The previous key is state, not a ref, because React's derive-state-from-props pattern
  // requires it: a ref written during render tears under concurrent rendering, where a render
  // can be thrown away and replayed.
  const identity = `${scope.orgSlug}/${scope.restaurantId}/${resourceKey}`;
  const [lastIdentity, setLastIdentity] = useState(identity);

  // Adjust-state-during-render (React's derive-state-from-props pattern): a different resource
  // OR a restaurant switch re-reads storage before painting, instead of one render painting
  // another resource's — or another tenant's — columns.
  if (lastIdentity !== identity) {
    setLastIdentity(identity);
    setStored(readColumnPrefs(resourceKey, columns, scope));
  }

  const configKeys = useMemo(() => columns.map((column) => column.key), [columns]);
  const alwaysVisibleKeys = useMemo(
    () => columns.filter((column) => column.alwaysVisible).map((column) => column.key),
    [columns]
  );
  const prefs = useMemo(
    () => reconcileColumnPrefs(configKeys, alwaysVisibleKeys, stored),
    [configKeys, alwaysVisibleKeys, stored]
  );

  const setPrefs = (next: ColumnPrefs) => {
    setStored(next);
    writeColumnPrefs(resourceKey, next, scope);
  };

  return [prefs, setPrefs];
}
